import { type FC, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { DashboardNarrativePanel } from "src/components/common";
import { useMockAiSummaryUsage } from "src/hooks/useMockAiSummaryUsage";
import { generateRestaurantExecutiveSummary, type RestaurantSummaryTab } from "../utils/restaurantExecutiveSummary";
import { useRestaurantFormatters } from "../utils/restaurantFormatters";
import type { RestaurantFilters, RestaurantMetrics } from "../utils/restaurantMetrics";

interface RestaurantAiExecutiveSummaryProps {
  tab: RestaurantSummaryTab;
  metrics: RestaurantMetrics;
  filters: RestaurantFilters;
}

const AI_SUMMARY_STORAGE_KEY = "restaurant_analytics_ai_summary_enabled";

const readStoredSummaryEnabled = (): boolean => {
  try {
    return localStorage.getItem(AI_SUMMARY_STORAGE_KEY) !== "false";
  } catch {
    return true;
  }
};

const writeStoredSummaryEnabled = (enabled: boolean) => {
  try {
    localStorage.setItem(AI_SUMMARY_STORAGE_KEY, String(enabled));
  } catch {
    // Storage can be unavailable (private mode, blocked site data) — the toggle
  }
};

const RestaurantAiExecutiveSummary: FC<RestaurantAiExecutiveSummaryProps> = ({
  tab,
  metrics,
  filters,
}) => {
  const { t } = useTranslation();
  const fmt = useRestaurantFormatters();
  const [analyzing, setAnalyzing] = useState(false);
  const [summaryEnabled, setSummaryEnabled] = useState(readStoredSummaryEnabled);

  const handleSummaryEnabledChange = useCallback((enabled: boolean) => {
    setSummaryEnabled(enabled);
    writeStoredSummaryEnabled(enabled);
  }, []);

  const filterKey = `${filters.startDate}|${filters.endDate}|${filters.branchId}|${filters.orderType}|${filters.channel}|${tab}`;

  useEffect(() => {
    if (!summaryEnabled) {
      setAnalyzing(false);
      return;
    }
    setAnalyzing(true);
    const id = window.setTimeout(() => setAnalyzing(false), 420);
    return () => window.clearTimeout(id);
  }, [filterKey, summaryEnabled]);

  const summaryUsage = useMockAiSummaryUsage("restaurant_analytics", filterKey, summaryEnabled);

  const summary = generateRestaurantExecutiveSummary({ tab, metrics, filters, fmt });

  return (
    <div className="restaurant-ai-summary">
      <DashboardNarrativePanel
        narrative={summary.hasData ? summary : null}
        loading={analyzing}
        defaultCollapsed
        title={t("restaurantAnalytics.aiSummary.title", { defaultValue: "AI Executive Summary" })}
        subtitle={
          summary.periodLabel
            ? t("restaurantAnalytics.aiSummary.subtitle", {
                defaultValue: "Powered by DataTram AI · {{period}}",
                period: summary.periodLabel,
              })
            : undefined
        }
        aiSummary={{
          enabled: summaryEnabled,
          featureAvailable: true,
          setEnabled: handleSummaryEnabledChange,
          usage: summaryUsage,
        }}
      />
    </div>
  );
};

export default RestaurantAiExecutiveSummary;
